// Cognito auth — thin wrapper over aws-amplify v6 modular auth so the
// library stays swappable. Pool/client ids come from the backend at runtime
// (/api/auth/config), so the bundle never needs a per-environment rebuild.

import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

export type SignInResult = "done" | "new-password";

export async function configureAuth(): Promise<void> {
  const res = await fetch("/api/auth/config");
  if (!res.ok) throw new Error("Couldn't load auth config");
  const cfg: { userPoolId: string; clientId: string } = await res.json();
  Amplify.configure({
    Auth: {
      Cognito: { userPoolId: cfg.userPoolId, userPoolClientId: cfg.clientId },
    },
  });
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<SignInResult> {
  const out = await signIn({ username: email.trim().toLowerCase(), password });
  if (out.isSignedIn) return "done";
  if (out.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
    return "new-password"; // invited user with a temporary password
  }
  throw new Error(`Unsupported sign-in step: ${out.nextStep.signInStep}`);
}

export async function completeNewPassword(newPassword: string): Promise<void> {
  const out = await confirmSignIn({ challengeResponse: newPassword });
  if (!out.isSignedIn) {
    throw new Error(`Couldn't finish sign-in (${out.nextStep.signInStep})`);
  }
}

/** Email a reset code to the account (silently succeeds for unknown emails —
 *  the pool is configured not to reveal which accounts exist). */
export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({ username: email.trim().toLowerCase() });
}

export async function confirmPasswordReset(
  email: string, code: string, newPassword: string,
): Promise<void> {
  await confirmResetPassword({
    username: email.trim().toLowerCase(),
    confirmationCode: code.trim(),
    newPassword,
  });
}

/** Current access token, refreshed by Amplify as needed; null = signed out. */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function hasSession(): Promise<boolean> {
  return (await getAccessToken()) !== null;
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}
